// Detailed budget items by F&B business type with realistic price ranges

export interface BudgetLineItem {
  item: string;
  lowEstimate: number;
  highEstimate: number;
  included: boolean;
  note: string;
}

export interface BudgetCategory {
  category: string;
  items: BudgetLineItem[];
}

const commonItems: BudgetCategory[] = [
  {
    category: "Licenses & Legal",
    items: [
      { item: "LLC / Business Registration", lowEstimate: 50, highEstimate: 500, included: true, note: "Varies by state" },
      { item: "Business License", lowEstimate: 50, highEstimate: 400, included: true, note: "City/county fee" },
      { item: "Sales Tax Permit", lowEstimate: 0, highEstimate: 50, included: true, note: "Free in most states" },
      { item: "Signage Permit", lowEstimate: 50, highEstimate: 500, included: true, note: "Required for exterior signs" },
      { item: "Fire Inspection", lowEstimate: 50, highEstimate: 200, included: true, note: "Fire marshal inspection" },
      { item: "Certificate of Occupancy", lowEstimate: 100, highEstimate: 300, included: true, note: "Building department" },
      { item: "EIN (Federal Tax ID)", lowEstimate: 0, highEstimate: 0, included: true, note: "Free from IRS" },
    ],
  },
  {
    category: "Insurance",
    items: [
      { item: "General Liability Insurance", lowEstimate: 500, highEstimate: 2000, included: true, note: "Per year" },
      { item: "Workers' Comp Insurance", lowEstimate: 800, highEstimate: 3000, included: true, note: "Per year, required with employees" },
      { item: "Property Insurance", lowEstimate: 500, highEstimate: 1500, included: true, note: "Per year" },
    ],
  },
  {
    category: "Marketing & Branding",
    items: [
      { item: "Logo & Brand Design", lowEstimate: 300, highEstimate: 2000, included: true, note: "Professional designer" },
      { item: "Signage (exterior)", lowEstimate: 500, highEstimate: 5000, included: true, note: "Depends on size and material" },
      { item: "Website", lowEstimate: 0, highEstimate: 2000, included: true, note: "Free via Squarespace or custom" },
      { item: "Grand Opening Marketing", lowEstimate: 500, highEstimate: 3000, included: true, note: "Flyers, social media ads, event" },
      { item: "Menu / Lookbook Printing", lowEstimate: 200, highEstimate: 800, included: true, note: "Design + print" },
    ],
  },
  {
    category: "Technology",
    items: [
      { item: "POS System", lowEstimate: 0, highEstimate: 2000, included: true, note: "Square (free) to Toast ($1,500+)" },
      { item: "Security Cameras", lowEstimate: 200, highEstimate: 1500, included: true, note: "4–8 camera system" },
      { item: "Internet Setup", lowEstimate: 100, highEstimate: 300, included: true, note: "Installation + first month" },
      { item: "Accounting Software", lowEstimate: 0, highEstimate: 360, included: true, note: "Wave (free) or QuickBooks ($30/mo)" },
    ],
  },
];

const restaurantItems: BudgetCategory[] = [
  {
    category: "Lease & Deposits",
    items: [
      { item: "Security Deposit (2 months)", lowEstimate: 8000, highEstimate: 30000, included: true, note: "Typically 2× monthly rent" },
      { item: "First Month's Rent", lowEstimate: 4000, highEstimate: 15000, included: true, note: "Depends on city & neighborhood" },
      { item: "Last Month's Rent", lowEstimate: 4000, highEstimate: 15000, included: true, note: "Often required upfront" },
      { item: "Lease Broker Fee", lowEstimate: 2000, highEstimate: 8000, included: true, note: "Typically 1 month's rent" },
    ],
  },
  {
    category: "Buildout & Construction",
    items: [
      { item: "Kitchen Buildout", lowEstimate: 40000, highEstimate: 150000, included: true, note: "Hood, plumbing, electrical" },
      { item: "Dining Room Renovation", lowEstimate: 15000, highEstimate: 60000, included: true, note: "Flooring, walls, lighting, paint" },
      { item: "Restroom Renovation", lowEstimate: 5000, highEstimate: 18000, included: true, note: "ADA compliance may be required" },
      { item: "HVAC Upgrade", lowEstimate: 8000, highEstimate: 25000, included: true, note: "Kitchen ventilation is critical" },
      { item: "Grease Trap / Grease Interceptor", lowEstimate: 1500, highEstimate: 8000, included: true, note: "Required by code for commercial kitchens — size depends on kitchen output" },
      { item: "Architect / Designer Fees", lowEstimate: 5000, highEstimate: 20000, included: true, note: "Floor plan and permits drawings" },
      { item: "Plumbing & Gas Work", lowEstimate: 5000, highEstimate: 18000, included: true, note: "Gas lines, floor drains" },
    ],
  },
  {
    category: "Kitchen Equipment",
    items: [
      { item: "Commercial Range / Stove", lowEstimate: 5000, highEstimate: 18000, included: true, note: "6-burner with oven" },
      { item: "Commercial Refrigerator", lowEstimate: 3000, highEstimate: 12000, included: true, note: "Reach-in or walk-in" },
      { item: "Walk-in Cooler / Freezer", lowEstimate: 5000, highEstimate: 15000, included: true, note: "Walk-in cooler + freezer combo" },
      { item: "Prep Tables (stainless)", lowEstimate: 1500, highEstimate: 4000, included: true, note: "2–4 tables" },
      { item: "Commercial Dishwasher", lowEstimate: 4000, highEstimate: 12000, included: true, note: "High-temp or chemical sanitizer" },
      { item: "Exhaust Hood & Fire Suppression", lowEstimate: 8000, highEstimate: 25000, included: true, note: "Required by fire code" },
      { item: "Smallwares (pots, pans, utensils)", lowEstimate: 3000, highEstimate: 8000, included: true, note: "Knives, cutting boards, containers" },
      { item: "Ice Machine", lowEstimate: 2000, highEstimate: 5000, included: true, note: "Essential for drinks & food storage" },
      { item: "Fryer / Flat Top Griddle", lowEstimate: 2000, highEstimate: 8000, included: true, note: "Commercial-grade" },
    ],
  },
  {
    category: "Dining Room & Front of House",
    items: [
      { item: "Tables & Chairs", lowEstimate: 5000, highEstimate: 25000, included: true, note: "Depends on seat count and quality" },
      { item: "Dinnerware, Glasses, Flatware", lowEstimate: 2000, highEstimate: 6000, included: true, note: "Plates, cups, silverware" },
      { item: "Host Stand & Decor", lowEstimate: 2000, highEstimate: 8000, included: true, note: "First impression matters" },
      { item: "Bar / Service Counter", lowEstimate: 3000, highEstimate: 15000, included: false, note: "If applicable" },
    ],
  },
  {
    category: "Food & Beverage Permits",
    items: [
      { item: "Food Service License", lowEstimate: 200, highEstimate: 1500, included: true, note: "County health department" },
      { item: "Health Inspection Fee", lowEstimate: 300, highEstimate: 800, included: true, note: "Pre-opening inspection" },
      { item: "ServSafe Certification", lowEstimate: 150, highEstimate: 300, included: true, note: "At least 1 certified manager" },
    ],
  },
  {
    category: "Initial Inventory",
    items: [
      { item: "Food Inventory (first 2 weeks)", lowEstimate: 5000, highEstimate: 15000, included: true, note: "Proteins, produce, pantry" },
      { item: "Beverage Inventory", lowEstimate: 1500, highEstimate: 5000, included: true, note: "Non-alcoholic + supplies" },
      { item: "Paper & Cleaning Supplies", lowEstimate: 800, highEstimate: 2500, included: true, note: "Napkins, cleaners, towels" },
    ],
  },
  {
    category: "Payroll (Pre-Opening, 2 weeks)",
    items: [
      { item: "Head Chef / Cook", lowEstimate: 2800, highEstimate: 5600, included: true, note: "Training & menu prep" },
      { item: "Line Cooks (2)", lowEstimate: 3200, highEstimate: 6400, included: true, note: "Kitchen training" },
      { item: "Servers / FOH (3)", lowEstimate: 3600, highEstimate: 7200, included: true, note: "Service training" },
      { item: "Dishwasher / Busser", lowEstimate: 1200, highEstimate: 2400, included: true, note: "Setup help + training" },
    ],
  },
  {
    category: "Working Capital (3 months)",
    items: [
      { item: "Operating Cash Reserve", lowEstimate: 30000, highEstimate: 80000, included: true, note: "Cover expenses while building revenue — critical for survival" },
    ],
  },
  {
    category: "Contingency (10–15%)",
    items: [
      { item: "Unexpected Costs Buffer", lowEstimate: 25000, highEstimate: 75000, included: true, note: "Always budget 10–20% extra — surprises are guaranteed" },
    ],
  },
];

const cafeItems: BudgetCategory[] = [
  {
    category: "Lease & Deposits",
    items: [
      { item: "Security Deposit", lowEstimate: 4000, highEstimate: 16000, included: true, note: "2× monthly rent" },
      { item: "First + Last Month Rent", lowEstimate: 4000, highEstimate: 16000, included: true, note: "Upfront requirement" },
    ],
  },
  {
    category: "Buildout",
    items: [
      { item: "Counter & Bar Construction", lowEstimate: 8000, highEstimate: 25000, included: true, note: "Service counter + display case" },
      { item: "Plumbing (espresso drain, sink)", lowEstimate: 3000, highEstimate: 10000, included: true, note: "3-compartment sink required" },
      { item: "Grease Trap / Grease Interceptor", lowEstimate: 1000, highEstimate: 4000, included: true, note: "May be required depending on food prep — smaller unit for cafés" },
      { item: "Interior Design & Paint", lowEstimate: 8000, highEstimate: 25000, included: true, note: "Lighting, flooring, walls" },
    ],
  },
  {
    category: "Equipment",
    items: [
      { item: "Espresso Machine", lowEstimate: 5000, highEstimate: 22000, included: true, note: "La Marzocca, Breville, or Rancilio" },
      { item: "Coffee Grinder (2)", lowEstimate: 1200, highEstimate: 4000, included: true, note: "Burr grinder for espresso + drip" },
      { item: "Drip / Batch Brewer", lowEstimate: 400, highEstimate: 1500, included: true, note: "For bulk coffee service" },
      { item: "Blenders (2)", lowEstimate: 400, highEstimate: 1200, included: true, note: "For smoothies / frappes" },
      { item: "Refrigerator (undercounter)", lowEstimate: 1500, highEstimate: 4000, included: true, note: "Milk, cream, prep items" },
      { item: "Pastry Display Case", lowEstimate: 1500, highEstimate: 5000, included: true, note: "Refrigerated or dry" },
      { item: "Ice Machine", lowEstimate: 1200, highEstimate: 3000, included: true, note: "For iced drinks" },
    ],
  },
  {
    category: "Furniture & Decor",
    items: [
      { item: "Tables & Chairs (15–25 seats)", lowEstimate: 4000, highEstimate: 12000, included: true, note: "Mix of tables and lounge" },
      { item: "Cups, Lids, Napkins, Sleeves", lowEstimate: 600, highEstimate: 1500, included: true, note: "First month supply" },
    ],
  },
  {
    category: "Initial Inventory",
    items: [
      { item: "Coffee Beans (first month)", lowEstimate: 1000, highEstimate: 3000, included: true, note: "From roaster" },
      { item: "Milk, Syrups, Toppings", lowEstimate: 600, highEstimate: 2000, included: true, note: "Oat, almond, whole, etc." },
      { item: "Pastries / Food Items", lowEstimate: 600, highEstimate: 2500, included: true, note: "Wholesale bakery or in-house" },
    ],
  },
  {
    category: "Payroll (Pre-Opening)",
    items: [
      { item: "Baristas (2–3)", lowEstimate: 2400, highEstimate: 5000, included: true, note: "1 week training" },
    ],
  },
  {
    category: "Working Capital (3 months)",
    items: [
      { item: "Operating Cash Reserve", lowEstimate: 15000, highEstimate: 40000, included: true, note: "Cover rent + payroll while building traffic" },
    ],
  },
  {
    category: "Contingency",
    items: [
      { item: "Buffer", lowEstimate: 10000, highEstimate: 30000, included: true, note: "10–15% of total" },
    ],
  },
];

const barItems: BudgetCategory[] = [
  {
    category: "Lease & Deposits",
    items: [
      { item: "Security Deposit", lowEstimate: 8000, highEstimate: 25000, included: true, note: "2× monthly rent" },
      { item: "First + Last Month Rent", lowEstimate: 8000, highEstimate: 25000, included: true, note: "Upfront" },
    ],
  },
  {
    category: "Buildout",
    items: [
      { item: "Bar Construction", lowEstimate: 15000, highEstimate: 50000, included: true, note: "Custom bar top, shelving, foot rail" },
      { item: "Plumbing & Drainage", lowEstimate: 5000, highEstimate: 15000, included: true, note: "Bar sinks, glass washer drain" },
      { item: "Grease Trap / Grease Interceptor", lowEstimate: 1500, highEstimate: 6000, included: true, note: "Required if kitchen serves food — size depends on output" },
      { item: "Lighting & Sound System", lowEstimate: 5000, highEstimate: 20000, included: true, note: "Ambiance is everything in a bar" },
      { item: "Interior Design", lowEstimate: 10000, highEstimate: 35000, included: true, note: "Walls, flooring, booths" },
    ],
  },
  {
    category: "Equipment",
    items: [
      { item: "Back Bar Coolers (2–3)", lowEstimate: 3000, highEstimate: 10000, included: true, note: "Glass door display coolers" },
      { item: "Draft Beer System", lowEstimate: 4000, highEstimate: 12000, included: true, note: "Taps, lines, kegerator" },
      { item: "Glass Washer", lowEstimate: 2000, highEstimate: 5000, included: true, note: "Under-counter commercial" },
      { item: "Ice Machine (large)", lowEstimate: 3000, highEstimate: 6000, included: true, note: "Bars use a LOT of ice" },
      { item: "Blenders, Shakers, Bar Tools", lowEstimate: 1000, highEstimate: 3000, included: true, note: "Jiggers, strainers, muddlers" },
      { item: "Glassware (assorted)", lowEstimate: 1500, highEstimate: 4000, included: true, note: "Pint, rocks, wine, cocktail" },
    ],
  },
  {
    category: "Alcohol Licenses",
    items: [
      { item: "Beer & Wine License", lowEstimate: 500, highEstimate: 2000, included: true, note: "Easier, faster approval" },
      { item: "Full Liquor License", lowEstimate: 3000, highEstimate: 20000, included: true, note: "Required for cocktails and spirits" },
      { item: "Federal TTB Permit", lowEstimate: 0, highEstimate: 0, included: true, note: "Free, required federally" },
    ],
  },
  {
    category: "Initial Inventory",
    items: [
      { item: "Liquor Inventory", lowEstimate: 6000, highEstimate: 18000, included: true, note: "Full bar opening stock" },
      { item: "Beer Inventory", lowEstimate: 2000, highEstimate: 5000, included: true, note: "Draft + bottles/cans" },
      { item: "Wine Inventory", lowEstimate: 2000, highEstimate: 8000, included: true, note: "By the glass + bottle list" },
      { item: "Mixers, Garnishes, Supplies", lowEstimate: 1000, highEstimate: 3000, included: true, note: "Soda, juice, fruit, bitters" },
    ],
  },
  {
    category: "Payroll (Pre-Opening)",
    items: [
      { item: "Bartenders (2–3)", lowEstimate: 3000, highEstimate: 6000, included: true, note: "Training week" },
      { item: "Barback", lowEstimate: 1200, highEstimate: 2500, included: true, note: "Support staff" },
    ],
  },
  {
    category: "Working Capital (3 months)",
    items: [
      { item: "Operating Cash Reserve", lowEstimate: 20000, highEstimate: 60000, included: true, note: "Cover rent + payroll while building clientele" },
    ],
  },
  {
    category: "Contingency",
    items: [
      { item: "Buffer", lowEstimate: 18000, highEstimate: 50000, included: true, note: "10–15% of total" },
    ],
  },
];

const bakeryItems: BudgetCategory[] = [
  {
    category: "Lease & Deposits",
    items: [
      { item: "Security Deposit", lowEstimate: 4000, highEstimate: 14000, included: true, note: "2× rent" },
      { item: "First + Last Month Rent", lowEstimate: 4000, highEstimate: 14000, included: true, note: "Upfront" },
    ],
  },
  {
    category: "Buildout",
    items: [
      { item: "Kitchen / Production Area", lowEstimate: 15000, highEstimate: 55000, included: true, note: "Ovens, ventilation, electrical" },
      { item: "Grease Trap / Grease Interceptor", lowEstimate: 1000, highEstimate: 4000, included: true, note: "Required if baking with fats/oils — smaller unit for bakeries" },
      { item: "Retail Front / Display Area", lowEstimate: 5000, highEstimate: 18000, included: true, note: "Counter, display cases" },
    ],
  },
  {
    category: "Equipment",
    items: [
      { item: "Commercial Deck Oven", lowEstimate: 6000, highEstimate: 25000, included: true, note: "Or convection oven" },
      { item: "Commercial Mixer (20–60 qt)", lowEstimate: 3000, highEstimate: 12000, included: true, note: "Hobart or equivalent" },
      { item: "Proof Box / Proofer", lowEstimate: 2000, highEstimate: 6000, included: true, note: "For bread / pastry dough" },
      { item: "Refrigerator + Freezer", lowEstimate: 3000, highEstimate: 8000, included: true, note: "For ingredients & storage" },
      { item: "Sheet Pans, Racks, Tools", lowEstimate: 1000, highEstimate: 3500, included: true, note: "Pans, cooling racks, piping bags" },
      { item: "Display Case (refrigerated)", lowEstimate: 2000, highEstimate: 6000, included: true, note: "For cakes, pastries" },
    ],
  },
  {
    category: "F&B Permits",
    items: [
      { item: "Food Service License", lowEstimate: 200, highEstimate: 1000, included: true, note: "Health department" },
      { item: "Health Inspection", lowEstimate: 200, highEstimate: 600, included: true, note: "Pre-opening" },
      { item: "ServSafe Cert", lowEstimate: 150, highEstimate: 300, included: true, note: "Manager certification" },
    ],
  },
  {
    category: "Initial Inventory",
    items: [
      { item: "Flour, Sugar, Butter, Eggs", lowEstimate: 1000, highEstimate: 3500, included: true, note: "First 2 weeks" },
      { item: "Packaging (boxes, bags, labels)", lowEstimate: 600, highEstimate: 2000, included: true, note: "Branded packaging" },
    ],
  },
  {
    category: "Payroll (Pre-Opening)",
    items: [
      { item: "Baker (1–2)", lowEstimate: 2400, highEstimate: 5000, included: true, note: "Recipe testing + training" },
      { item: "Counter Staff (1)", lowEstimate: 1200, highEstimate: 2400, included: true, note: "Training" },
    ],
  },
  {
    category: "Working Capital (3 months)",
    items: [
      { item: "Operating Cash Reserve", lowEstimate: 12000, highEstimate: 35000, included: true, note: "Cover expenses while building clientele" },
    ],
  },
  {
    category: "Contingency",
    items: [
      { item: "Buffer", lowEstimate: 8000, highEstimate: 25000, included: true, note: "10–15%" },
    ],
  },
];

export function getBudgetItemsForType(businessType: string): BudgetCategory[] {
  const typeMap: Record<string, BudgetCategory[]> = {
    "restaurant": restaurantItems,
    "coffee-shop": cafeItems,
    "bakery": bakeryItems,
    "bakery-cafe": [...cafeItems.slice(0, 2), ...bakeryItems.slice(2, 4), ...cafeItems.slice(2)],
    "bar": barItems,
    "wine-shop": barItems,
    "ice-cream-shop": cafeItems,
    "pizzeria": restaurantItems,
    "juice-bar": cafeItems,
    "fast-casual": restaurantItems,
    "brunch-spot": restaurantItems,
    "food-truck": [
      {
        category: "Vehicle",
        items: [
          { item: "Food Truck (used)", lowEstimate: 30000, highEstimate: 80000, included: true, note: "Pre-built kitchen truck" },
          { item: "Vehicle Wrap / Branding", lowEstimate: 3000, highEstimate: 7000, included: true, note: "Custom graphics" },
          { item: "Generator", lowEstimate: 2000, highEstimate: 6000, included: true, note: "For off-grid cooking" },
        ],
      },
      {
        category: "Kitchen Equipment",
        items: [
          { item: "Flat Top Grill / Fryer", lowEstimate: 2000, highEstimate: 6000, included: true, note: "Primary cooking" },
          { item: "Refrigeration (truck)", lowEstimate: 1000, highEstimate: 3000, included: true, note: "Under-counter unit" },
          { item: "Smallwares", lowEstimate: 500, highEstimate: 2000, included: true, note: "Utensils, containers" },
        ],
      },
      {
        category: "Permits",
        items: [
          { item: "Mobile Food Vendor License", lowEstimate: 400, highEstimate: 2000, included: true, note: "City-specific" },
          { item: "Health Inspection", lowEstimate: 200, highEstimate: 600, included: true, note: "Mobile kitchen inspection" },
          { item: "Commissary Kitchen Rental (3 months)", lowEstimate: 1200, highEstimate: 3600, included: true, note: "Required in most cities" },
        ],
      },
      {
        category: "Initial Inventory & Payroll",
        items: [
          { item: "Food Inventory (2 weeks)", lowEstimate: 1500, highEstimate: 4000, included: true, note: "Starting stock" },
          { item: "Staff (1–2 helpers)", lowEstimate: 1500, highEstimate: 3500, included: true, note: "Training week" },
        ],
      },
      {
        category: "Working Capital (3 months)",
        items: [
          { item: "Operating Cash Reserve", lowEstimate: 8000, highEstimate: 20000, included: true, note: "Cover expenses while building routes" },
        ],
      },
      {
        category: "Contingency",
        items: [
          { item: "Buffer", lowEstimate: 5000, highEstimate: 15000, included: true, note: "10–15%" },
        ],
      },
    ],
    "hotel": [
      {
        category: "Property Acquisition / Lease",
        items: [
          { item: "Security Deposit (3–6 months)", lowEstimate: 30000, highEstimate: 300000, included: true, note: "Depends on property size and market" },
          { item: "First Month's Rent / Mortgage", lowEstimate: 15000, highEstimate: 150000, included: true, note: "Or purchase down payment" },
        ],
      },
      {
        category: "Buildout & Renovation",
        items: [
          { item: "Guest Room Renovation (per room)", lowEstimate: 5000, highEstimate: 30000, included: true, note: "Multiply by room count" },
          { item: "Lobby & Common Areas", lowEstimate: 20000, highEstimate: 150000, included: true, note: "Reception, lounge, corridors" },
          { item: "HVAC System", lowEstimate: 10000, highEstimate: 100000, included: true, note: "Central or split systems" },
          { item: "Elevator Installation / Upgrade", lowEstimate: 20000, highEstimate: 100000, included: false, note: "If multi-story property" },
          { item: "Pool & Spa Construction", lowEstimate: 15000, highEstimate: 80000, included: false, note: "If offering pool/spa amenities" },
        ],
      },
      {
        category: "FF&E (Furniture, Fixtures & Equipment)",
        items: [
          { item: "Linen & Bedding (per room)", lowEstimate: 300, highEstimate: 1500, included: true, note: "Sheets, pillows, towels, duvets" },
          { item: "Guest Room Furniture (per room)", lowEstimate: 1500, highEstimate: 8000, included: true, note: "Bed, desk, TV, seating, storage" },
          { item: "Bathroom Fixtures", lowEstimate: 500, highEstimate: 3000, included: true, note: "Per room — vanity, shower, toiletries" },
          { item: "Lobby Furniture & Decor", lowEstimate: 5000, highEstimate: 30000, included: true, note: "Seating, art, lighting" },
          { item: "Laundry Equipment", lowEstimate: 5000, highEstimate: 30000, included: true, note: "Commercial washers/dryers or outsource" },
        ],
      },
      {
        category: "Technology & Systems",
        items: [
          { item: "PMS (Property Management System)", lowEstimate: 3000, highEstimate: 15000, included: true, note: "Opera, Cloudbeds, Mews, etc." },
          { item: "Key Card / Smart Lock System", lowEstimate: 3000, highEstimate: 20000, included: true, note: "Per door + management system" },
          { item: "Channel Manager / OTA Integration", lowEstimate: 500, highEstimate: 3000, included: true, note: "Connects to Booking.com, Expedia, Airbnb" },
          { item: "In-Room Entertainment / WiFi", lowEstimate: 2000, highEstimate: 15000, included: true, note: "Smart TVs, streaming, enterprise WiFi" },
          { item: "Security & CCTV System", lowEstimate: 3000, highEstimate: 15000, included: true, note: "Cameras, access control, safes" },
        ],
      },
      {
        category: "Hotel Permits & Licensing",
        items: [
          { item: "Lodging License", lowEstimate: 200, highEstimate: 2000, included: true, note: "State/county lodging permit" },
          { item: "Liquor License", lowEstimate: 1000, highEstimate: 14000, included: false, note: "If serving alcohol in bar/restaurant" },
          { item: "Fire Safety Certificate", lowEstimate: 200, highEstimate: 1500, included: true, note: "Multi-story fire inspection" },
          { item: "Elevator Inspection", lowEstimate: 200, highEstimate: 500, included: true, note: "Annual per elevator" },
          { item: "Pool & Spa Permit", lowEstimate: 100, highEstimate: 500, included: false, note: "If pool/spa facility" },
          { item: "ADA Compliance Costs", lowEstimate: 500, highEstimate: 10000, included: true, note: "Retrofitting for accessibility" },
        ],
      },
      {
        category: "Staff Uniforms & Supplies",
        items: [
          { item: "Staff Uniforms (all departments)", lowEstimate: 2000, highEstimate: 10000, included: true, note: "Front desk, housekeeping, maintenance, F&B" },
          { item: "Guest Amenities (initial stock)", lowEstimate: 1000, highEstimate: 5000, included: true, note: "Toiletries, robes, slippers, minibar" },
          { item: "Cleaning Supplies & Equipment", lowEstimate: 1000, highEstimate: 5000, included: true, note: "Housekeeping carts, chemicals, vacuums" },
        ],
      },
      {
        category: "Brand & Marketing",
        items: [
          { item: "Brand Standards Compliance", lowEstimate: 5000, highEstimate: 50000, included: false, note: "If affiliated with a hotel brand/chain" },
          { item: "Website & Booking Engine", lowEstimate: 2000, highEstimate: 10000, included: true, note: "Direct booking capability" },
          { item: "Photography & Virtual Tours", lowEstimate: 1000, highEstimate: 5000, included: true, note: "Professional photos for OTAs and website" },
        ],
      },
      {
        category: "Pre-Opening Payroll",
        items: [
          { item: "General Manager (2 months)", lowEstimate: 8000, highEstimate: 16000, included: true, note: "Hire early for setup oversight" },
          { item: "Front Desk Staff Training (2 weeks)", lowEstimate: 3000, highEstimate: 8000, included: true, note: "PMS training, guest service standards" },
          { item: "Housekeeping Training (1 week)", lowEstimate: 2000, highEstimate: 6000, included: true, note: "Room standards, cleaning protocols" },
        ],
      },
      {
        category: "Contingency",
        items: [
          { item: "Unexpected Costs Buffer (15%)", lowEstimate: 25000, highEstimate: 100000, included: true, note: "Hotels have higher surprise costs" },
        ],
      },
    ],
  };
  
  const specific = typeMap[businessType] || restaurantItems;
  return [...specific, ...commonItems];
}

export function calculateBudgetTotals(categories: BudgetCategory[]): { totalLow: number; totalHigh: number } {
  let totalLow = 0;
  let totalHigh = 0;
  for (const cat of categories) {
    for (const item of cat.items) {
      if (item.included) {
        totalLow += item.lowEstimate;
        totalHigh += item.highEstimate;
      }
    }
  }
  return { totalLow, totalHigh };
}

/**
 * Category-specific cost multiplier mapping.
 * Maps budget category names to the relevant market multiplier type.
 */
type MultiplierType = "rent" | "buildout" | "wage" | "general";

const CATEGORY_MULTIPLIER_MAP: Record<string, MultiplierType> = {
  "Lease & Deposits": "rent",
  "Property Acquisition / Lease": "rent",
  "Buildout & Construction": "buildout",
  "Buildout": "buildout",
  "Buildout & Renovation": "buildout",
  "Kitchen Buildout": "buildout",
  "Kitchen Equipment": "general",
  "Equipment": "general",
  "FF&E (Furniture, Fixtures & Equipment)": "general",
  "Dining Room & Front of House": "general",
  "Furniture & Decor": "general",
  "Payroll (Pre-Opening, 2 weeks)": "wage",
  "Payroll (Pre-Opening)": "wage",
  "Pre-Opening Payroll": "wage",
  "Initial Inventory & Payroll": "wage",
  "Staff Uniforms & Supplies": "wage",
  "Insurance": "general",
  "Vehicle": "general",
  "Contingency (10%)": "general",
  "Contingency": "general",
};

interface MarketMultipliers {
  rentMultiplier: number;
  wageMultiplier: number;
  buildoutMultiplier: number;
  generalMultiplier: number;
}

/**
 * Apply realistic market-based cost adjustments to budget categories.
 * All base prices are US averages. This scales them for the specific city market.
 */
/**
 * Simple deterministic hash for consistent jitter per item name.
 */
function itemHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Apply realistic market-based cost adjustments and break round numbers
 * into specific, context-aware amounts that feel real (e.g. $4,275 not $4,000).
 */
export function applyMarketMultipliers(
  categories: BudgetCategory[],
  multipliers: MarketMultipliers,
  sqft: number = 1200,
  businessType: string = "restaurant"
): BudgetCategory[] {
  // Use sqft and businessType to seed additional variation
  const contextSeed = itemHash(businessType) + sqft;

  return categories.map(cat => {
    const type = CATEGORY_MULTIPLIER_MAP[cat.category] || "general";
    let mult: number;
    switch (type) {
      case "rent": mult = multipliers.rentMultiplier; break;
      case "buildout": mult = multipliers.buildoutMultiplier; break;
      case "wage": mult = multipliers.wageMultiplier; break;
      default: mult = multipliers.generalMultiplier; break;
    }
    return {
      ...cat,
      items: cat.items.map(item => {
        const h = itemHash(item.item + cat.category) ^ contextSeed;
        // Jitter between -8% and +12% to break round numbers
        const jitterLow = 0.92 + (h % 200) / 1000;       // 0.92–1.12
        const jitterHigh = 0.94 + ((h >> 8) % 180) / 1000; // 0.94–1.12
        const rawLow = item.lowEstimate * mult * jitterLow;
        const rawHigh = item.highEstimate * mult * jitterHigh;
        // Round to nearest 25 for amounts > 1000, nearest 5 for smaller
        const snap = (v: number) => {
          if (v <= 0) return 0;
          if (v < 100) return Math.round(v / 5) * 5;
          if (v < 1000) return Math.round(v / 25) * 25;
          if (v < 10000) return Math.round(v / 50) * 50;
          return Math.round(v / 250) * 250;
        };
        return {
          ...item,
          lowEstimate: snap(rawLow),
          highEstimate: Math.max(snap(rawHigh), snap(rawLow)),
        };
      }),
    };
  });
}
